/**
 * Hindi translations of the service content defined in servicesData.ts.
 * Keyed by service slug; only the human-readable fields are translated.
 * `icon` and `slug` always come from the English source of truth.
 */
export interface ServiceContentHi {
  group: string;
  title: string;
  tagline: string;
  intro: string;
  highlights: { title: string; desc: string }[];
  deliverables: string[];
  useCases: string[];
}

export const SERVICE_CONTENT_HI: Record<string, ServiceContentHi> = {
  "ai-development": {
    group: "AI और मशीन लर्निंग",
    title: "AI डेवलपमेंट",
    tagline: "AI को अपने बिज़नेस में काम पर लगाएं।",
    intro:
      "हम ऐसा AI डिज़ाइन और बनाते हैं जो एक असली काम पूरा करता है — ग्राहकों के जवाब देना, दस्तावेज़ पढ़ना, पैटर्न पहचानना या यह बताना कि आगे क्या होगा। आपको काम करता हुआ सॉफ़्टवेयर मिलता है, कोई साइंस प्रोजेक्ट नहीं।",
    highlights: [
      {
        title: "कस्टम AI समाधान",
        desc: "आपके डेटा और वर्कफ़्लो के हिसाब से बनाया गया, किसी सामान्य टेम्पलेट से नहीं।",
      },
      {
        title: "स्मार्ट डॉक्यूमेंट रीडिंग",
        desc: "इनवॉइस, फॉर्म और PDF को साफ़, उपयोगी जानकारी में बदलें।",
      },
      {
        title: "प्रेडिक्शन और इनसाइट्स",
        desc: "मांग का अनुमान लगाएं, धोखाधड़ी पकड़ें या महंगे होने से पहले जोखिम चिह्नित करें।",
      },
      {
        title: "आपका डेटा आपका ही रहता है",
        desc: "क्लाउड या ऑन-प्रिमाइस — आप चुनें कि आपका डेटा कहाँ रहे।",
      },
    ],
    deliverables: [
      "डिस्कवरी वर्कशॉप और एक स्पष्ट AI रोडमैप",
      "काम करता हुआ AI मॉडल या फ़ीचर जो आपके प्रोडक्ट में जुड़ा हो",
      "सटीकता और नतीजों की निगरानी के लिए डैशबोर्ड",
      "आपकी टीम के लिए ट्रेनिंग और हैंडओवर",
    ],
    useCases: [
      "ग्राहकों के संदेशों को अपने आप छांटें और जवाब दें",
      "कागज़ी काम पढ़ें और डिजिटल करें",
      "सेल्स, स्टॉक या मांग का अनुमान लगाएं",
      "हर ग्राहक को सही प्रोडक्ट सुझाएं",
    ],
  },
  "ai-agents": {
    group: "AI और मशीन लर्निंग",
    title: "AI एजेंट्स",
    tagline: "काम पूरा करने वाले डिजिटल साथी।",
    intro:
      "एजेंट्स सिर्फ़ जवाब नहीं देते — वे काम करते हैं। वे जानकारी खोजते हैं, अगला कदम उठाते हैं, आपके सिस्टम अपडेट करते हैं और काम पूरा होने तक चलते रहते हैं, चौबीसों घंटे।",
    highlights: [
      {
        title: "सपोर्ट एजेंट्स",
        desc: "आम सवाल संभालें और मुश्किल वाले इंसान को भेजें।",
      },
      {
        title: "सेल्स एजेंट्स",
        desc: "लीड्स को योग्य बनाएं, मीटिंग बुक करें और अपने आप फॉलो-अप करें।",
      },
      {
        title: "बैक-ऑफ़िस एजेंट्स",
        desc: "रिकॉर्ड अपडेट करें, टिकट बनाएं और छूटी जानकारी का पीछा करें।",
      },
      {
        title: "आपके टूल्स से जुड़े",
        desc: "एजेंट्स उन्हीं ऐप्स में काम करते हैं जो आप रोज़ इस्तेमाल करते हैं।",
      },
    ],
    deliverables: [
      "आपके असली बातचीत के फ़्लो के हिसाब से बनाया गया एजेंट",
      "सुरक्षित गार्डरेल्स और इंसान को हैंडओवर करने के नियम",
      "आपके ही कंटेंट से बना नॉलेज बेस",
      "बातचीत और नतीजों की रिपोर्टिंग",
    ],
    useCases: [
      "24/7 पहली पंक्ति का कस्टमर सपोर्ट",
      "तुरंत लीड क्वालिफिकेशन और बुकिंग",
      "अपने आप ऑर्डर और अकाउंट संबंधी पूछताछ",
      "आपके स्टाफ़ के लिए इंटरनल हेल्पडेस्क",
    ],
  },
  "api-development": {
    group: "सॉफ़्टवेयर और इंटीग्रेशन",
    title: "API डेवलपमेंट",
    tagline: "सिस्टम्स को जोड़ें ताकि डेटा खुद चले।",
    intro:
      "हम सुरक्षित API और कनेक्शन बनाते हैं जिनसे आपके ऐप्स, पार्टनर और टूल्स एक-दूसरे से बात कर पाते हैं — ताकि जानकारी हाथ से कॉपी करने की जरूरत न रहे।",
    highlights: [
      {
        title: "REST और GraphQL APIs",
        desc: "साफ़, अच्छी तरह डॉक्यूमेंटेड एंडपॉइंट्स जिन पर आपकी टीम भरोसा कर सके।",
      },
      {
        title: "थर्ड-पार्टी कनेक्शन",
        desc: "पेमेंट, मैसेजिंग, मैप्स, अकाउंटिंग और बहुत कुछ।",
      },
      {
        title: "शुरुआत से सुरक्षित",
        desc: "कीज़, रोल्स और ऑडिट ट्रेल्स पहले दिन से शामिल।",
      },
      {
        title: "तेज़ और भरोसेमंद",
        desc: "असली ट्रैफ़िक संभालने के लिए बनाया गया, बिना बिखरे।",
      },
    ],
    deliverables: [
      "API डिज़ाइन और डॉक्यूमेंटेशन",
      "डेवलपमेंट, टेस्टिंग और डिप्लॉयमेंट",
      "ऑथेंटिकेशन और एक्सेस कंट्रोल",
      "लगातार मॉनिटरिंग और सपोर्ट",
    ],
    useCases: [
      "आपकी दुकान और अकाउंटिंग के बीच ऑर्डर सिंक करें",
      "वेबसाइट से लीड्स सीधे आपके CRM में भेजें",
      "पार्टनर्स के साथ डेटा सुरक्षित रूप से साझा करें",
      "एक बैकएंड से अपना मोबाइल ऐप चलाएं",
    ],
  },
  "crm-integration": {
    group: "सॉफ़्टवेयर और इंटीग्रेशन",
    title: "CRM सेटअप और इंटीग्रेशन",
    tagline: "हर लीड और ग्राहक एक जगह।",
    intro:
      "हम आपका CRM सेटअप, कस्टमाइज़ और जोड़ते हैं ताकि कुछ भी छूटे नहीं। लीड्स सही पाइपलाइन में आती हैं, फॉलो-अप समय पर होते हैं और आपकी टीम को एक साफ़ तस्वीर दिखती है।",
    highlights: [
      {
        title: "सेटअप और माइग्रेशन",
        desc: "अपने मौजूदा कॉन्टैक्ट्स इतिहास खोए बिना ले जाएं।",
      },
      {
        title: "आपकी प्रक्रिया के अनुसार",
        desc: "पाइपलाइन, स्टेज और फ़ील्ड आपके सेलिंग तरीके के हिसाब से।",
      },
      {
        title: "अपने आप फॉलो-अप",
        desc: "रिमाइंडर, टास्क और संदेश अपने आप ट्रिगर होते हैं।",
      },
      {
        title: "हर जगह जुड़ा",
        desc: "वेबसाइट, फॉर्म, ईमेल, SMS और सोशल — सब जुड़ते हैं।",
      },
    ],
    deliverables: [
      "आपकी सेल्स प्रक्रिया के लिए सेट किया गया CRM",
      "डेटा माइग्रेशन और सफ़ाई",
      "ऑटोमेटेड फॉलो-अप वर्कफ़्लो",
      "टीम ट्रेनिंग और अपनाने में सहायता",
    ],
    useCases: [
      "वेबसाइट या विज्ञापन से आई कोई लीड कभी न खोएं",
      "हर रेप को एक ही साफ़ पाइपलाइन दें",
      "समय पर फॉलो-अप अपने आप ट्रिगर करें",
      "एक डैशबोर्ड पर अपनी पूरी सेल्स तस्वीर देखें",
    ],
  },
  "system-integrations": {
    group: "सॉफ़्टवेयर और इंटीग्रेशन",
    title: "सिस्टम इंटीग्रेशन",
    tagline: "आपके सभी टूल्स को एक साथ चलाएं।",
    intro:
      "ज़्यादातर बिज़नेस एक दर्जन अलग-अलग ऐप्स पर चलते हैं। हम उन्हें जोड़ देते हैं ताकि डेटा अपने आप बहे, बजाय दोबारा टाइप किए जाने के।",
    highlights: [
      {
        title: "एंड-टू-एंड वर्कफ़्लो",
        desc: "एक टूल में एक काम दूसरे टूल में सही कदम ट्रिगर करता है।",
      },
      {
        title: "अब दोबारा एंट्री नहीं",
        desc: "जानकारी एक बार डालें; जहाँ चाहिए वहाँ अपडेट हो जाए।",
      },
      {
        title: "आपके पास जो है उसी के साथ",
        desc: "नए खरीदने से पहले हम आपके मौजूदा टूल्स जोड़ते हैं।",
      },
      {
        title: "भरोसेमंद और मॉनिटर किया गया",
        desc: "किसी कनेक्शन पर ध्यान देने की जरूरत हो तो अलर्ट मिलता है।",
      },
    ],
    deliverables: [
      "आपके मौजूदा टूल्स और डेटा फ़्लो का नक्शा",
      "आपके मुख्य सिस्टम्स के बीच बनाए गए कनेक्शन",
      "एरर हैंडलिंग और नोटिफिकेशन",
      "सब कुछ कैसे जुड़ा है इसका डॉक्यूमेंटेशन",
    ],
    useCases: [
      "वेबसाइट पूछताछ → CRM → ईमेल फॉलो-अप",
      "ऑनलाइन ऑर्डर → इनवॉइसिंग → अकाउंट्स",
      "पेमेंट मिला → ग्राहक को सूचित करें",
      "फॉर्म भरा → आपकी टीम के लिए टास्क बनाएं",
    ],
  },
  "cloud-infrastructure": {
    group: "क्लाउड और इन्फ्रास्ट्रक्चर",
    title: "क्लाउड, सर्वर और इन्फ्रास्ट्रक्चर",
    tagline: "भरोसेमंद होस्टिंग, और जरूरत पर एक्सपर्ट्स।",
    intro:
      "क्लाउड डिप्लॉयमेंट से लेकर डेडिकेटेड सर्वर और अतिरिक्त मदद तक, हम आपके सॉफ़्टवेयर को ठोस ठिकाना और बढ़ने के लिए जरूरी इन्फ्रास्ट्रक्चर देते हैं।",
    highlights: [
      {
        title: "क्लाउड डिप्लॉयमेंट",
        desc: "AWS, Azure या Google Cloud पर सेटअप करें और बढ़ाएं।",
      },
      {
        title: "सर्वर और हार्डवेयर",
        desc: "जिन मशीनों पर आप चलते हैं उन्हें तैयार, कॉन्फ़िगर और सुरक्षित करें।",
      },
      {
        title: "इन्फ्रा रिसोर्स हायर करें",
        desc: "अनुभवी DevOps और इन्फ्रास्ट्रक्चर इंजीनियर जोड़ें।",
      },
      {
        title: "मॉनिटरिंग और बैकअप",
        desc: "कुछ गड़बड़ हो तो तुरंत जानें — और जल्दी रिकवर करें।",
      },
    ],
    deliverables: [
      "आपके वर्कलोड के लिए डिज़ाइन किया गया इन्फ्रास्ट्रक्चर",
      "ऑटोमेटेड डिप्लॉयमेंट पाइपलाइन",
      "मॉनिटरिंग, अलर्ट और रोज़ाना बैकअप",
      "सिक्योरिटी हार्डनिंग और लागत ऑप्टिमाइज़ेशन",
    ],
    useCases: [
      "अपने ऐप को सुरक्षित तरीके से क्लाउड पर ले जाएं",
      "व्यस्त सीज़न के लिए क्षमता बढ़ाएं",
      "अपनी IT टीम में छोटी अवधि की कमी पूरी करें",
      "अहम सिस्टम्स को ऑनलाइन और बैकअप रखें",
    ],
  },
  "messaging-services": {
    group: "कम्युनिकेशन",
    title: "SMS और ईमेल सर्विसेज़",
    tagline: "ग्राहकों तक वहीं पहुंचें जहां वे असल में देखते हैं।",
    intro:
      "भरोसेमंद, हाई-डिलीवरी SMS और ईमेल जो सीधे आपके वर्कफ़्लो से जुड़ते हैं — रिमाइंडर, रसीदें, ऑफ़र और फॉलो-अप, सही समय पर अपने आप भेजे जाते हैं।",
    highlights: [
      {
        title: "SMS कैंपेन",
        desc: "डिलीवरी ट्रैकिंग के साथ समय-संवेदनशील संदेश।",
      },
      {
        title: "बड़े पैमाने पर ईमेल",
        desc: "न्यूज़लेटर्स, सीक्वेंस और ट्रांज़ैक्शनल ईमेल।",
      },
      {
        title: "ट्रिगर-आधारित भेजना",
        desc: "संदेश आपके अपने बिज़नेस इवेंट्स से चलते हैं।",
      },
      {
        title: "टेम्पलेट और एनालिटिक्स",
        desc: "ब्रांडेड टेम्पलेट्स, ओपन और क्लिक रिपोर्टिंग के साथ।",
      },
    ],
    deliverables: [
      "सेंडर सेटअप और डिलीवरेबिलिटी की सर्वोत्तम प्रैक्टिस",
      "SMS और ईमेल के लिए ब्रांडेड टेम्पलेट्स",
      "आपके वर्कफ़्लो से जुड़े ऑटोमेटेड सीक्वेंस",
      "डिलीवरी, ओपन और क्लिक रिपोर्टिंग",
    ],
    useCases: [
      "अपॉइंटमेंट और पेमेंट रिमाइंडर",
      "वेलकम और ऑनबोर्डिंग संदेश",
      "आपकी कस्टमर लिस्ट के लिए प्रोमोशन",
      "ऑर्डर और डिलीवरी नोटिफिकेशन",
    ],
  },
  "website-development": {
    group: "ऐप्स और ऑटोमेशन",
    title: "वेबसाइट डेवलपमेंट",
    tagline: "तेज़, आधुनिक वेबसाइट जो ग्राहक लाएं।",
    intro:
      "मार्केटिंग साइट्स, लैंडिंग पेज और वेब ऐप्स जो तेज़ खुलते हैं, बढ़िया दिखते हैं और विज़िटर्स को पूछताछ में बदलते हैं — पहले दिन से आपके CRM और एनालिटिक्स से जुड़े।",
    highlights: [
      {
        title: "मार्केटिंग और लैंडिंग पेज",
        desc: "जानकारी देने और क्लिक पाने के लिए डिज़ाइन किए गए।",
      },
      {
        title: "कस्टम वेब ऐप्स",
        desc: "पोर्टल्स, डैशबोर्ड और कस्टमर टूल्स।",
      },
      {
        title: "खोजे जाने के लिए बने",
        desc: "सर्च-फ्रेंडली स्ट्रक्चर और साफ़ परफ़ॉर्मेंस।",
      },
      {
        title: "लॉन्च से जुड़े",
        desc: "फॉर्म, चैट और ट्रैकिंग आपके सिस्टम्स को फ़ीड करते हैं।",
      },
    ],
    deliverables: [
      "आपकी साइट या वेब ऐप का डिज़ाइन और निर्माण",
      "मोबाइल-फर्स्ट, हर डिवाइस पर रिस्पॉन्सिव",
      "आपके CRM से जुड़े फॉर्म और चैट",
      "एनालिटिक्स और लॉन्च सपोर्ट",
    ],
    useCases: [
      "किसी कैंपेन के लिए हाई-कन्वर्टिंग लैंडिंग पेज",
      "नई रूप में मार्केटिंग वेबसाइट",
      "कस्टमर पोर्टल या बुकिंग टूल",
      "ऑनलाइन कोट या चेकआउट फ़्लो",
    ],
  },
  "mobile-app-development": {
    group: "ऐप्स और ऑटोमेशन",
    title: "मोबाइल ऐप डेवलपमेंट",
    tagline: "ऐप्स जो आपके ग्राहक और टीम पसंद करें।",
    intro:
      "आपके ग्राहकों, स्टाफ़ या फील्ड टीम के लिए नेटिव जैसा महसूस होने वाले iOS और Android ऐप्स — एक बैकएंड के साथ जो सबको सिंक में रखता है।",
    highlights: [
      {
        title: "कस्टमर ऐप्स",
        desc: "बुकिंग, ऑर्डरिंग, लॉयल्टी और अकाउंट एक्सेस।",
      },
      {
        title: "स्टाफ़ और फील्ड ऐप्स",
        desc: "काम, चेकलिस्ट और अपडेट कहीं से भी।",
      },
      {
        title: "एक बैकएंड, दो प्लेटफ़ॉर्म",
        desc: "एक बार बनाएं और iPhone व Android दोनों तक पहुंचें।",
      },
      {
        title: "ऑफ़लाइन-फ्रेंडली",
        desc: "सिग्नल जाने पर भी काम करता रहता है।",
      },
    ],
    deliverables: [
      "ऐप डिज़ाइन, निर्माण और स्टोर सबमिशन",
      "साझा बैकएंड और एडमिन डैशबोर्ड",
      "पुश नोटिफिकेशन और एनालिटिक्स",
      "लॉन्च के बाद अपडेट और सपोर्ट",
    ],
    useCases: [
      "आपके ग्राहकों के लिए बुकिंग ऐप",
      "काम और फ़ोटो के लिए फील्ड टीम ऐप",
      "लॉयल्टी या मेंबरशिप ऐप",
      "चलते-फिरते अप्रूवल के लिए इंटरनल ऐप",
    ],
  },
  "mobile-workflow-automation": {
    group: "ऐप्स और ऑटोमेशन",
    title: "मोबाइल वर्कफ़्लो ऑटोमेशन",
    tagline: "आपकी प्रक्रियाएं, फोन से चलती हुईं।",
    intro:
      "रोज़ की दिनचर्या — अप्रूवल, रिपोर्ट, चेक-इन, जॉब अपडेट — को आसान मोबाइल वर्कफ़्लो में बदलें जो खुद चलते हैं और आपकी टीम को आगे बढ़ाते रहते हैं।",
    highlights: [
      {
        title: "चलते-फिरते अप्रूवल",
        desc: "मैनेजर डेस्क से नहीं, नोटिफिकेशन से अप्रूव करें।",
      },
      {
        title: "जॉब और टास्क अपडेट",
        desc: "स्टाफ़ फील्ड से स्थिति लाइव अपडेट करें।",
      },
      {
        title: "ऑटो रिपोर्ट्स",
        desc: "रोज़ाना सारांश अपने आप बनकर भेजे जाते हैं।",
      },
      {
        title: "आपके टूल्स के साथ",
        desc: "आपके CRM, शीट्स और अकाउंटिंग को फ़ीड करता है।",
      },
    ],
    deliverables: [
      "आपकी मौजूदा प्रक्रिया से बनाए गए वर्कफ़्लो",
      "मोबाइल फॉर्म और अप्रूवल स्टेप्स",
      "अपने आप नोटिफिकेशन और रिपोर्ट्स",
      "आपके मौजूदा सिस्टम्स के साथ इंटीग्रेशन",
    ],
    useCases: [
      "फील्ड स्टाफ़ अपने फोन से जॉब अपडेट करें",
      "मैनेजर खर्च या छुट्टी तुरंत अप्रूव करें",
      "रोज़ाना सेल्स और विज़िट रिपोर्ट अपने आप भेजी जाएं",
      "चेकलिस्ट फील्ड में भरें और लॉग करें",
    ],
  },
};
